import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import {
  Globe, Layers, Landmark, Coins, MapPin, Cable, Trees, Waves, Compass, TrendingUp,
  Users, Smartphone, Network, Library, Database, ShieldAlert, Sparkles, BookCheck,
  Trophy, BadgeCheck, Download, PlayCircle, Loader2, CheckCircle2, XCircle, ToggleRight, ToggleLeft,
} from "lucide-react";

const STREAMS = [
  { key: "nsdi", label: "NSDI360", icon: Layers },
  { key: "land_admin", label: "Land Administration", icon: Landmark },
  { key: "valuation", label: "Valuation & Revenue360", icon: Coins },
  { key: "address", label: "Address360", icon: MapPin },
  { key: "utility", label: "Utility & Twin360", icon: Cable },
  { key: "environment", label: "Environment & Climate", icon: Trees },
  { key: "marine", label: "Marine & Water", icon: Waves },
  { key: "planning", label: "National Planning", icon: Compass },
  { key: "development", label: "Development Monitoring", icon: TrendingUp },
  { key: "market", label: "Market & Investment", icon: TrendingUp },
  { key: "citizen", label: "Citizen & OpenData", icon: Users },
  { key: "mobile", label: "Mobile Field Ops", icon: Smartphone },
  { key: "integration", label: "Government Hub", icon: Network },
  { key: "knowledge", label: "Knowledge & Policy", icon: Library },
  { key: "data_governance", label: "Data Governance", icon: Database },
  { key: "performance_risk", label: "Performance & Risk", icon: ShieldAlert },
  { key: "ai_intel", label: "AI Land Intelligence", icon: Sparkles },
  { key: "standards", label: "Standards & Compliance", icon: BookCheck },
  { key: "benchmarking", label: "Global Benchmarking", icon: Trophy },
  { key: "acceptance", label: "Global Acceptance", icon: BadgeCheck },
];

const path = { nsdi: "/nsdi", land_admin: "/land-admin", valuation: "/valuation",
  address: "/address", utility: "/utility", environment: "/environment",
  marine: "/marine", planning: "/planning", development: "/development",
  market: "/market", citizen: "/citizen", mobile: "/mobile",
  integration: "/integration", knowledge: "/knowledge",
  data_governance: "/data-governance", performance_risk: "/performance-risk",
  ai_intel: "/ai-intel", standards: "/standards", benchmarking: "/benchmarking" };

const KPI = ({ label, value, hint, tone }) => (
  <div className="cad-panel px-3 py-2">
    <div className="font-mono-cad text-[9px] uppercase tracking-[0.22em] text-[#7C93A5]">{label}</div>
    <div className={"font-display font-bold mt-0.5 " + (tone || "text-[#F6F8FA]") + " text-[18px]"}>{value ?? "—"}</div>
    {hint && <div className="font-mono-cad text-[10px] text-[#B3C2CC] mt-0.5">{hint}</div>}
  </div>
);

const KV = ({ k, v }) => (
  <div className="border-b cad-hairline pb-1 flex items-center gap-2 text-[12px]">
    <div className="font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5] flex-1 truncate">{k}</div>
    <div className="font-mono-cad text-[#F6F8FA] break-all text-right max-w-[65%]">{typeof v === "object" ? JSON.stringify(v) : String(v)}</div>
  </div>
);

const Section = ({ title, children }) => (
  <div className="cad-panel p-4">
    <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">{title}</div>
    <div className="mt-3 space-y-1">{children}</div>
  </div>
);

const StreamPanel = ({ streamKey }) => {
  const p = path[streamKey];
  const q = useQuery({ queryKey: ["global-" + streamKey], queryFn: async () => (await api.get("/cadastre360/global" + p)).data, enabled: !!p });
  if (!p) return null;
  if (q.isLoading) return <div className="cad-panel p-6 text-[#B3C2CC] text-[13px]">Loading…</div>;
  if (q.isError) return <div className="cad-panel p-6 text-[#E67E7E] text-[13px]">Stream unavailable · {String(q.error?.response?.status || "")}</div>;
  const d = q.data || {};
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid={"global-stream-" + streamKey}>
      {Object.entries(d).map(([key, val]) => {
        if (key === "generated_at") return null;
        if (Array.isArray(val)) {
          return (
            <Section key={key} title={key.replace(/_/g, " ")}>
              <div className="space-y-1 max-h-[280px] overflow-y-auto">
                {val.map((item, i) => (
                  <div key={key + "-" + i} className="border-b cad-hairline pb-1 text-[11px] text-[#DCE3E8]">
                    {typeof item === "object" ? (
                      <div className="grid grid-cols-2 gap-1">
                        {Object.entries(item).map(([kk, vv]) => (
                          <div key={kk} className="truncate"><span className="font-mono-cad text-[10px] text-[#7C93A5]">{kk}</span> <span className="text-[#F6F8FA]">{String(vv)}</span></div>
                        ))}
                      </div>
                    ) : String(item)}
                  </div>
                ))}
              </div>
            </Section>
          );
        }
        if (typeof val === "object" && val !== null) {
          return (
            <Section key={key} title={key.replace(/_/g, " ")}>
              {Object.entries(val).map(([kk, vv]) => <KV key={kk} k={kk} v={vv} />)}
            </Section>
          );
        }
        return (
          <div key={key} className="cad-panel px-3 py-2">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#7C93A5]">{key.replace(/_/g, " ")}</div>
            <div className="font-display font-bold text-[#F6F8FA] mt-0.5">{typeof val === "boolean" ? (val ? "yes" : "no") : String(val)}</div>
          </div>
        );
      })}
    </div>
  );
};

const AcceptancePanel = () => {
  const qc = useQueryClient();
  const flags = useQuery({ queryKey: ["global-flags"], queryFn: async () => (await api.get("/cadastre360/global/feature-flags")).data });
  const reports = useQuery({ queryKey: ["global-acc-reports"], queryFn: async () => (await api.get("/cadastre360/global/acceptance/reports")).data });
  const readiness = useQuery({ queryKey: ["global-readiness"], queryFn: async () => (await api.get("/cadastre360/global/global-readiness/report")).data });
  const run = useMutation({
    mutationFn: async () => (await api.post("/cadastre360/global/acceptance/run", {})).data,
    onSuccess: (d) => { toast.success("Global " + d.overall + " · " + d.passed + "/" + d.scope_size); qc.invalidateQueries({ queryKey: ["global-acc-reports"] }); },
  });
  const toggle = useMutation({
    mutationFn: async ({ name, enabled }) => (await api.post("/cadastre360/global/feature-flags/" + name, { enabled })).data,
    onSuccess: () => { toast.success("Flag updated"); qc.invalidateQueries({ queryKey: ["global-flags"] }); },
  });
  const latest = (reports.data || [])[0];
  const r = readiness.data;
  return (
    <div className="space-y-3">
      {r && (
        <div className="cad-panel p-6 flex items-center gap-6">
          <div className="text-[64px] font-display font-bold text-[#C99A2E] leading-none">{r.maturity?.overall}%</div>
          <div>
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">Global readiness</div>
            <div className="font-display text-2xl font-bold text-[#F6F8FA] mt-1">{r.verdict}</div>
            <div className="text-[12px] text-[#B3C2CC] mt-1">Peer percentile {r.peer_percentile}</div>
          </div>
        </div>
      )}
      <div className="cad-panel p-4 flex flex-wrap items-center gap-3">
        <div className="text-[12px] text-[#B3C2CC] flex-1 min-w-[220px] leading-relaxed">
          Run the Global Acceptance Programme across every stream and download the sovereign Global Readiness Certificate.
        </div>
        <button onClick={() => run.mutate()} disabled={run.isPending}
                className="cad-btn-primary px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1"
                data-testid="global-acc-run">
          {run.isPending ? <Loader2 size={11} className="animate-spin" /> : <PlayCircle size={11} />} Run global acceptance
        </button>
        <a href={API + "/cadastre360/global/acceptance/certificate.pdf"} target="_blank" rel="noreferrer"
           className="cad-btn-outline px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1"
           data-testid="global-acc-cert">
          <Download size={11} /> Global Readiness Certificate (PDF)
        </a>
      </div>
      {latest && (
        <div className="cad-panel p-4">
          <div className="flex items-center gap-3">
            <span className={"cad-chip text-[10px] uppercase " + (latest.overall === "PASS" ? "cad-chip-green" : "cad-chip-red")}>{latest.overall}</span>
            <span className="font-mono-cad text-[11px] text-[#F6F8FA]">{latest.id}</span>
            <span className="font-mono-cad text-[11px] text-[#B3C2CC]">{latest.passed}/{latest.scope_size} streams</span>
            <span className="font-mono-cad text-[10px] text-[#7C93A5] ml-auto">{latest.run_at?.slice(0, 19).replace("T", " ")}</span>
          </div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-1">
            {(latest.checks || []).map((c, i) => (
              <div key={c.stream + "-" + i} className="border-b cad-hairline pb-1 flex items-start gap-2">
                {c.ok ? <CheckCircle2 size={12} className="text-[#6BB88A] mt-0.5" /> : <XCircle size={12} className="text-[#E67E7E] mt-0.5" />}
                <div>
                  <div className="text-[11px] text-[#F6F8FA]">{c.label}</div>
                  <div className="text-[10px] text-[#7C93A5] font-mono-cad">{c.note}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="cad-panel p-4">
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">Stream feature flags</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mt-3">
          {Object.entries(flags.data || {}).map(([k, v]) => (
            <div key={k} className="border-b cad-hairline pb-1 flex items-center gap-2">
              <div className="text-[11px] text-[#F6F8FA] flex-1 font-mono-cad">{k.replace("cadastre360_stream_", "")}</div>
              <button onClick={() => toggle.mutate({ name: k, enabled: !v })}
                      className="cad-btn-outline px-2 py-0.5 text-[10px] rounded-sm inline-flex items-center gap-1"
                      data-testid={"global-flag-" + k}>
                {v ? <ToggleRight size={11} /> : <ToggleLeft size={11} />} {v ? "on" : "off"}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const GlobalGovernance = () => {
  const [tab, setTab] = useState("nsdi");
  return (
    <div className="p-6 space-y-4" data-testid="page-global-governance">
      <div className="flex items-start gap-3">
        <Globe size={32} className="text-[#C99A2E]" />
        <div>
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#C99A2E]">
            Cadastre360 · Global Land Governance Programme · v1.4
          </div>
          <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-0.5">Sovereign Digital Transformation Centre</h1>
          <div className="text-[12.5px] text-[#B3C2CC] mt-1 max-w-4xl">
            Twenty internationally-benchmarked streams consolidated into one coherent architecture. Every stream is additive,
            feature-flag governed, zero-regression compliant, and independently deployable.
          </div>
        </div>
      </div>

      <div className="cad-panel px-2 py-2 flex flex-wrap gap-1 sticky top-2 z-10" data-testid="global-tabs">
        {STREAMS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={"global-tab-" + t.key}
                    className={"px-2.5 py-1.5 rounded-sm text-[10.5px] font-mono-cad uppercase tracking-[0.12em] inline-flex items-center gap-1.5 " +
                               (active ? "bg-[#145A7A]/60 text-[#F6F8FA]" : "text-[#B3C2CC] hover:bg-[#145A7A]/25")}>
              <Icon size={11} className="text-[#C99A2E]" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "acceptance" ? <AcceptancePanel /> : <StreamPanel streamKey={tab} />}
    </div>
  );
};
